/**
 * Transcript cleaner — removes filler words, collapses repeats, trims silences.
 *
 * Public API:
 *   window.__acCleaner.clean(words, opts) → { words, removed, secsCut, removedRanges }
 *     opts: { lang, removeFillers, collapseRepeats, trimSilences, silenceThresholdSec }
 *
 *   window.__acCleaner.detect(words, opts) → { fillers, silences, repeats, totalSecs, totalWords }
 *     Returns a *plan* (what would be removed) without mutating anything.
 *     fillers   = [{wordIndex, text, reason:'filler'|'repeat', start, end}]
 *     silences  = [{start, end, secs}]
 *     repeats   = same shape as fillers, but reason:'repeat'
 *     totalSecs = sum of trimmed-silence seconds
 *     totalWords = count of words that would be removed (fillers + repeats)
 *
 *   window.__acCleaner.apply(words, plan, opts) → { words, removedWords, secsCut, removedRanges }
 *     Executes the plan, optionally with overrides:
 *       opts.keepWordIndices = Set of word-indices the user said "keep" — they
 *         get excluded from removal even if the plan says drop them. This is
 *         the hook __acFillerPreview uses for review-before-commit.
 *       opts.trimSilences / opts.silenceThresholdSec — same as clean().
 *
 * The cleaner is purely transcript-side — it never touches the source video.
 * For burn-in / SRT export the cleaned `words` array drops filler entries and
 * shifts subsequent words' timings backwards by the duration that was removed,
 * unless `keepTimings:true` (default false) — keeping original timings means
 * the cleaned transcript is for display only and the burned video keeps any
 * filler audible. Most users want timings shifted (= silence trimming) so
 * keepTimings defaults to false.
 *
 * Filler dictionaries are conservative — only well-known disfluencies. The
 * goal is to never strip meaningful content, even at the cost of leaving
 * some fillers behind.
 */
(function(){
    'use strict';

    /* Per-language filler word sets. Lowercase, no punctuation. */
    var FILLERS = {
        en: ['um','uh','umm','uhh','er','erm','hmm','ah','eh','mm','mhm','uh-huh','like','okay','ok','right','well','y\'know','you-know','i-mean','sort-of','kind-of','basically','literally','actually','obviously','honestly','anyway','anyways','so','yeah'],
        ru: ['э','ээ','эээ','м','мм','ммм','ну','ээ','то-есть','значит','короче','типа','блин','ну-вот','вот','как-бы','в-общем','собственно','прямо','реально','короче-говоря','так-сказать','значит-так','просто','этот-самый','такие-дела'],
        uk: ['е','ее','еее','м','мм','ммм','ну','тобто','значить','коротше','типу','ось','отож','як-би','знаєш','розумієш','власне','взагалі','насправді','цей','той'],
        tr: ['şey','yani','işte','aslında','hani','e','ee','eee','ıı','ııı','hıı','hı','aa','mmm','ha','tabii','ya','kısacası','neyse','bir-de','ne-bileyim','gibi','falan','filan','yani-işte','demek-ki','aynen','tamam'],
        de: ['äh','ähm','hm','hmm','also','halt','quasi','sozusagen','irgendwie','genau','tatsächlich','eigentlich','sozusagen','sag-mal','weißt-du','naja','ja-also'],
        es: ['eh','este','pues','o-sea','digamos','tipo','bueno','vale','vale-vale','la-verdad','realmente','sabes','o-sea-que'],
        pt: ['hum','é','tipo','sabe','tá','né','então','assim','tipo-assim','entendeu','digamos','pois','quer-dizer','ahn','hum-hum','na-verdade'],
        fr: ['euh','heu','hum','ben','bah','quoi','donc','tu-vois','enfin','genre','en-fait','vraiment','franchement','écoute','tu-sais'],
        it: ['ehm','mmm','cioè','tipo','allora','insomma','praticamente','diciamo','niente','sai','un-attimo','dunque','beh','vabbè'],
        ja: ['えーと','えっと','あの','その','まあ','うん','うーん','なんか','ていうか'],
        ko: ['음','어','아','그','저기','뭐','이제','그니까','그러니까','뭐랄까','약간','진짜','그냥'],
        zh: ['嗯','呃','啊','那个','这个','就是','然后','其实','所以','怎么说'],
        id: ['eh','em','hm','anu','gitu','kayak','kayaknya','ya','lah','dong','sih','kan','udah','ya-udah','ya-gitu'],
        ar: ['آه','يعني','اه','اممم','هاي','بصراحة','شو-اسمه','يعني-كده','والله','بس'],
    };

    function fillersForLang(lang) {
        if (!lang) return FILLERS.en;
        var key = String(lang).toLowerCase().slice(0, 2);
        return FILLERS[key] || FILLERS.en;
    }

    function normaliseWord(t) {
        return String(t || '')
            .toLowerCase()
            .replace(/[’']/g, "'")     // curly apostrophe → straight
            .replace(/[,.?!:;…]/g, '') // strip ,.?!:;…
            .replace(/\s+/g, '-')
            .trim();
    }

    /* Build the filler dictionary (per-language + user-supplied). Shared by
       detect() and clean() / apply(). */
    function buildFillerSet(opts) {
        var fillers = fillersForLang(opts && opts.lang).slice();
        if (opts && Array.isArray(opts.customFillers)) {
            for (var i = 0; i < opts.customFillers.length; i++) {
                var n = normaliseWord(opts.customFillers[i]);
                if (n) fillers.push(n);
            }
        }
        var set = Object.create(null);
        for (var k = 0; k < fillers.length; k++) set[fillers[k]] = true;
        return set;
    }

    /**
     * detect(words, opts) — non-mutating preview pass.
     *
     * Returns a plan describing WHAT cleanup would do, without applying it.
     * The plan is consumed by:
     *   - __acFillerPreview to render inline pending decorations + keep-pills
     *   - apply() to materialise the cleanup
     *
     * Word indices in the returned `fillers` array refer to positions in the
     * INPUT `words` array (not the output). Both filler-rule and repeat-rule
     * hits are emitted in `fillers` (with a `reason` field discriminating
     * them) so the UI can render one consistent list of "candidate" deletions.
     */
    function detect(words, opts) {
        opts = opts || {};
        var removeFillers    = opts.removeFillers !== false;
        var collapseRepeats  = opts.collapseRepeats !== false;
        var trimSilences     = opts.trimSilences !== false;
        var silenceThreshold = opts.silenceThresholdSec != null ? opts.silenceThresholdSec : 1.5;
        var fillerSet = buildFillerSet(opts);

        var fillers = [];
        var silences = [];
        var totalSecs = 0;

        if (!Array.isArray(words) || !words.length) {
            return { fillers: fillers, silences: silences, repeats: [], totalSecs: 0, totalWords: 0 };
        }

        var prevNorm = '';
        var prevEndOriginal = words[0].start;
        for (var i = 0; i < words.length; i++) {
            var w = words[i];
            if (w && w.isEmoji) { prevNorm = ''; continue; }
            var norm = normaliseWord(w && w.text);
            // Filler rule
            if (removeFillers && fillerSet[norm]) {
                fillers.push({
                    wordIndex: i,
                    text: w.text,
                    reason: 'filler',
                    start: w.start,
                    end: w.end,
                });
                prevNorm = norm;
                continue;
            }
            // Repeat rule — same normalised token as previous *kept* word.
            // We compare against prevNorm which only updates on kept words so
            // "the the the" reports two repeats, not one.
            if (collapseRepeats && norm && norm === prevNorm) {
                fillers.push({
                    wordIndex: i,
                    text: w.text,
                    reason: 'repeat',
                    start: w.start,
                    end: w.end,
                });
                continue;
            }
            // Silence rule — gap from prevEndOriginal to this word's start.
            if (trimSilences && typeof w.start === 'number') {
                var gap = w.start - prevEndOriginal;
                if (gap > silenceThreshold) {
                    var cut = gap - silenceThreshold;
                    var sStart = prevEndOriginal + silenceThreshold;
                    var sEnd = w.start;
                    silences.push({ start: sStart, end: sEnd, secs: cut });
                    totalSecs += cut;
                }
            }
            prevNorm = norm;
            if (typeof w.end === 'number') prevEndOriginal = w.end;
        }

        var repeats = fillers.filter(function(f){ return f.reason === 'repeat'; });
        return {
            fillers: fillers,                    // includes both filler+repeat hits
            silences: silences,
            repeats: repeats,
            totalSecs: Math.round(totalSecs * 10) / 10,
            totalWords: fillers.length,
        };
    }

    /**
     * apply(words, plan, opts) — materialise the plan into a cleaned word list.
     *
     * `plan` may be a plan from detect() OR null/omitted (in which case we
     * recompute it from `opts`).
     *
     * `opts.keepWordIndices` — array or Set of word-indices the user opted to
     * KEEP (i.e. skip removal even if plan says drop). This is the
     * review-before-commit hook.
     *
     * `opts.trimSilences` / `opts.silenceThresholdSec` — re-evaluated here
     * because the user may also have flipped the silence toggle in the
     * preview banner.
     */
    function apply(words, plan, opts) {
        opts = opts || {};
        if (!Array.isArray(words) || !words.length) {
            return { words: [], removedWords: 0, secsCut: 0, removedRanges: [] };
        }
        if (!plan) plan = detect(words, opts);

        var keepSet = Object.create(null);
        var keepIn = opts.keepWordIndices;
        if (keepIn) {
            if (typeof keepIn.forEach === 'function') {
                keepIn.forEach(function(idx){ keepSet[idx] = true; });
            } else if (Array.isArray(keepIn)) {
                for (var ki = 0; ki < keepIn.length; ki++) keepSet[keepIn[ki]] = true;
            }
        }

        // Build a quick lookup: wordIndex → plan entry (skip if user kept).
        var removeMap = Object.create(null);
        for (var pi = 0; pi < plan.fillers.length; pi++) {
            var ent = plan.fillers[pi];
            if (!keepSet[ent.wordIndex]) removeMap[ent.wordIndex] = ent;
        }

        var trimSilences     = opts.trimSilences !== false;
        var silenceThreshold = opts.silenceThresholdSec != null ? opts.silenceThresholdSec : 1.5;

        var out = [];
        var removedWords = 0;
        var secsCutSilence = 0;
        var removedRanges = [];
        var prevEndOriginal = words[0].start;

        for (var i = 0; i < words.length; i++) {
            var w = words[i];
            if (w && w.isEmoji) { out.push(w); continue; }
            if (removeMap[i]) {
                removedWords++;
                if (typeof w.start === 'number' && typeof w.end === 'number' && w.end > w.start) {
                    removedRanges.push([w.start, w.end]);
                }
                continue;
            }
            // Build the shifted copy.
            var nw = { text: w.text, start: w.start, end: w.end };
            for (var kk in w) if (kk !== 'text' && kk !== 'start' && kk !== 'end') nw[kk] = w[kk];
            if (trimSilences && typeof w.start === 'number') {
                var gap = w.start - prevEndOriginal;
                if (gap > silenceThreshold) {
                    var cut = gap - silenceThreshold;
                    secsCutSilence += cut;
                    var sStart = prevEndOriginal + silenceThreshold;
                    var sEnd = w.start;
                    if (sEnd > sStart) removedRanges.push([sStart, sEnd]);
                    nw.start -= cut;
                    nw.end -= cut;
                }
            }
            out.push(nw);
            if (typeof w.end === 'number') prevEndOriginal = w.end;
        }

        var result = {
            words: out,
            removedWords: removedWords,
            secsCut: Math.round(secsCutSilence * 10) / 10,
            removedRanges: removedRanges,
        };
        try { if (window.__acState) window.__acState.lastCleanerRun = result; } catch (_) {}
        return result;
    }

    function clean(words, opts) {
        opts = opts || {};
        var removeFillers     = opts.removeFillers !== false;
        var collapseRepeats   = opts.collapseRepeats !== false;
        var trimSilences      = opts.trimSilences !== false;
        var silenceThreshold  = opts.silenceThresholdSec != null ? opts.silenceThresholdSec : 1.5;
        var fillers = fillersForLang(opts.lang).slice();
        // Merge user-supplied custom fillers (one per line, free-form). They
        // are normalised the same way as runtime tokens (lowercase, no punct,
        // spaces → '-') so spacy phrases like "kind of" still match.
        if (Array.isArray(opts.customFillers)) {
            for (var i = 0; i < opts.customFillers.length; i++) {
                var n = normaliseWord(opts.customFillers[i]);
                if (n) fillers.push(n);
            }
        }
        var fillerSet = Object.create(null);
        fillers.forEach(function(f){ fillerSet[f] = true; });

        if (!Array.isArray(words) || !words.length) return { words: [], removedWords: 0, secsCut: 0, removedRanges: [] };

        var out = [];
        var removedWords = 0;
        var secsCutSilence = 0;
        // Phase C addition — per-rule range collection so __acMagicCut can
        // feed an FFmpeg select/aselect filter that ACTUALLY cuts the video
        // (Submagic Magic Cut / VEED Magic Cut parity). Each entry is a pair
        // of seconds on the ORIGINAL timeline that should be removed from the
        // burned MP4. Caller is responsible for merging adjacent ranges.
        var removedRanges = [];
        // Use the original-timeline previous end so silence-trim ranges are
        // anchored on the source video, not the post-shift caption time.
        var prevEnd = words[0].start;
        var prevEndOriginal = words[0].start;
        var prevNorm = '';

        for (var i = 0; i < words.length; i++) {
            var w = words[i];
            // Emoji entries are layout-only and never cleaned.
            if (w.isEmoji) {
                out.push(w);
                continue;
            }
            var norm = normaliseWord(w.text);

            // Rule 1 — filler removal. Track the original-timeline range so
            // the burn pipeline can excise these spans from audio+video.
            if (removeFillers && fillerSet[norm]) {
                removedWords++;
                if (typeof w.start === 'number' && typeof w.end === 'number' && w.end > w.start) {
                    removedRanges.push([w.start, w.end]);
                }
                continue;
            }

            // Rule 2 — collapse adjacent repeats ("the the", "I I", "так так").
            if (collapseRepeats && norm && norm === prevNorm) {
                removedWords++;
                if (typeof w.start === 'number' && typeof w.end === 'number' && w.end > w.start) {
                    removedRanges.push([w.start, w.end]);
                }
                continue;
            }

            // Rule 3 — silence trimming. We don't drop the word; we shift its
            // start/end backwards if the gap from prevEnd is larger than threshold.
            // The silence span itself is recorded as a removedRange on the
            // ORIGINAL timeline ([prevEndOriginal + silenceThreshold, w.start]).
            var nw = { text: w.text, start: w.start, end: w.end };
            for (var k in w) if (k !== 'text' && k !== 'start' && k !== 'end') nw[k] = w[k];
            if (trimSilences) {
                var gap = w.start - prevEndOriginal;
                if (gap > silenceThreshold) {
                    var cut = gap - silenceThreshold;
                    secsCutSilence += cut;
                    var silenceCutStart = prevEndOriginal + silenceThreshold;
                    var silenceCutEnd = w.start;
                    if (silenceCutEnd > silenceCutStart) {
                        removedRanges.push([silenceCutStart, silenceCutEnd]);
                    }
                    nw.start -= cut;
                    nw.end -= cut;
                }
            }
            out.push(nw);
            prevEnd = nw.end;
            prevEndOriginal = w.end;
            prevNorm = norm;
        }

        var result = {
            words: out,
            removedWords: removedWords,
            secsCut: Math.round(secsCutSilence * 10) / 10,
            removedRanges: removedRanges,
        };
        // Surface the last-run result onto window.__acState so __acMagicCut
        // can read it on burn without re-running the cleaner. Pipeline
        // populates state.lastCleanerRun after each cleaner invocation; this
        // mirror keeps standalone test scripts (which never go through the
        // pipeline) able to read removedRanges directly.
        try {
            if (window.__acState) window.__acState.lastCleanerRun = result;
        } catch (_) {}
        return result;
    }

    window.__acCleaner = {
        clean: clean,
        detect: detect,
        apply: apply,
        fillersForLang: fillersForLang,
        normaliseWord: normaliseWord,
    };
})();
